import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Camera, FileText, Grid3x3, ImageUp, ScanBarcode, SwitchCamera, X, ZoomIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCamera } from '@/lib/camera';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { prefersCameraCapture } from '@/lib/platform';
import { decodeFailureMessage, looksLikeImage } from '@/lib/image-source';
import { captureFingerprint, sha256Fingerprint } from '@/lib/fingerprint';
import { downscaleForUpload } from '@/lib/downscale-image';
import { runQualityGate, sampleImage } from '@/lib/quality-gate';
import { adviseOnQuality, gradeLabel, type QualityAdvice, type QualityGrade } from '@/lib/quality-advice';
import { decodeBarcodeFromImage } from '@/lib/barcode';
import {
  enqueueAndDeliver,
  enqueueTranscriptNote,
  processVoiceNoteQueue,
  removeQueuedCapture,
  type DeliveryState,
} from '@/lib/offline';
import {
  MAX_FRAMES,
  addFrame,
  clearFrames,
  holdForDecision,
  listFrames,
  removeFrame,
  type AddFrameOutcome,
  type CaptureFrame,
} from '@/lib/capture-frames';
import { getSessionId } from '@/lib/session';
import { readStoredWorkflow, workflowTitle } from '@/lib/capture-workflows';
import { getCaptureJob } from '@/lib/api/capture';
import { LiveTranscriptBox } from '@/components/voice/LiveTranscriptBox';
import { knownJobName, rememberCurrentJob } from '@/components/jobs/current-job';
import type { CaptureMode, QualityGateResult } from '@/lib/api/types';

type SingleShotMode = Extract<CaptureMode, 'photo' | 'document'>;

/**
 * CA-03: Capture viewfinder (wireframes/capture-app.html #ca03). Process
 * step 2. Opened from a job (`/jobs/:jobId/capture`, CAPTURE_JOBS.md
 * section 6), and every capture sent from here carries that job's id. On shutter: sample pixels for the on-device quality gate,
 * compute the sha256 fingerprint BEFORE anything touches the network
 * (SC3 note 2), try an on-device barcode decode, and add the frame to the
 * capture being built.
 *
 * One capture carries up to three frames, because an operator reads a part
 * number off one face of a pump and its rating plate off another, and the
 * extraction pipeline reads every frame it is sent. The frames are collected
 * here and the whole set goes to the offline queue in one action (SC3
 * note 1), which is the only place this screen sends anything.
 *
 * The gate result is always attached and never blocks the capture (SC3
 * note 3). A frame the gate wants to stop routes to CA-04 for the operator's
 * decision, and a frame kept there joins the set carrying `passed: false`,
 * which travels to the server with the rest.
 *
 * An image can also come off the device rather than the lens. The wireframe
 * draws a 360px phone, where the camera is the only sensible source, but the
 * same app opens on a warehouse tablet and on a desktop that often has no
 * usable rear camera at all, which left those machines unable to capture
 * anything. A chosen file runs the identical pipeline, because the fingerprint,
 * the quality gate and the barcode decode all read pixels and none of them
 * cares where the pixels came from. Where there is no camera the file picker
 * is the primary control rather than a fallback hidden behind a dead shutter.
 *
 * Which source leads is decided by the device, not by waiting for the camera
 * to fail. A touch device starts its camera on mount, because the operator is
 * already holding it up to the item. A pointer device starts nothing and
 * leads with the file picker, because requesting `getUserMedia` there only
 * raises a permission prompt for a webcam aimed at the person reviewing.
 */
/**
 * Set on the navigation back from CA-04 when the operator kept a frame the
 * quality gate had failed. It carries what became of that frame, because a
 * capture that is already full or already holds the same photograph does not
 * take it, and the decision screen cannot reach into this one's state to say
 * so.
 */
interface ViewfinderEntryState {
  keptFromRetake?: AddFrameOutcome;
}

/**
 * The job the held frames and the held voice note were collected for.
 * Both outlive this screen, because CA-04 is a route and going there
 * unmounts it; neither may follow the operator into a different job.
 */
let heldForJob: string | null = null;

/** A live voice note (CAPTURE_JOBS.md section 3) saved before the capture
 *  is sent. It travels with the capture once the frames go. */
interface HeldNote {
  transcript: string;
  durationMs: number;
}
let heldNote: HeldNote | null = null;

/** Drops everything held for another job before this one starts. */
function claimHeldFor(jobId: string): void {
  if (heldForJob !== jobId) {
    clearFrames();
    heldNote = null;
    heldForJob = jobId;
  }
}

/** What `POST /capture/upload-url` accepts for a document-mode frame
 *  beside a photograph (INTERFACES.md 15.6): a single-page PDF, the form a
 *  compliance certificate or a supplier invoice already exists in. Multi
 *  page is refused server-side, with a message naming the limit. */
const DOCUMENT_CONTENT_TYPE = 'application/pdf';

/** Whether a chosen file is the PDF this screen's document mode accepts,
 *  by declared type or, where a platform reports none, by extension. */
function looksLikePdf(file: File): boolean {
  if (file.type === DOCUMENT_CONTENT_TYPE) return true;
  if (file.type !== '') return false;
  return /\.pdf$/i.test(file.name);
}

/** What the screen says about a frame offered to the capture. */
const OUTCOME_MESSAGE: Record<AddFrameOutcome, string | null> = {
  added: null,
  full: `This capture already holds ${MAX_FRAMES} frames. Send it, or remove one first.`,
  duplicate: 'That is the same photograph as a frame you already have. Shoot the item from another angle.',
};

const KEPT_MESSAGE: Record<AddFrameOutcome, string> = {
  added: 'Kept the frame the quality check flagged. It is in this capture.',
  full: `This capture already holds ${MAX_FRAMES} frames, so that one was not kept. Send it, then shoot again.`,
  duplicate: 'This capture already holds that photograph, so the frame was not kept.',
};

/**
 * What stands in the frame once a capture is queued: the lead frame, the
 * quality grade with its suggestions, and where the capture got to. The
 * proof of concept kept the frame on screen with its assessment attached
 * under it, and this is that surface.
 *
 * The frame is where the operator is already looking, which is the reason
 * the confirmation moved into it. Under the controls it sat around 900px
 * down a 390px-wide screen, so a capture on a phone appeared to do nothing
 * at all.
 */
interface CapturedShot {
  url: string;
  /** The lead frame's own type, so the preview can tell a PDF apart from a
   *  photograph rather than handing every frame to an `<img>` tag. */
  contentType: string;
  advice: QualityAdvice | null;
  /** How many frames went up as this capture. */
  frameCount: number;
  delivery: DeliveryState | 'uploading';
  /** The `queue` row's own key, present as soon as the capture is sent.
   *  The held voice note keys to it until the capture has a server id. */
  localId: string;
  /** Set once the capture has arrived, which is what CA-05 is keyed on. */
  captureId?: string;
  /** The API's own words when it refused the capture (HTTP 402). */
  message?: string;
}

/**
 * The grade carries a colour only where it asks for something. A frame that
 * graded well with one check short is a note for the next shot, and painting
 * it the same orange as a poor one teaches the operator to ignore both.
 */
const GRADE_TONE: Record<QualityGrade, string> = {
  excellent: 'text-success',
  good: 'text-success',
  acceptable: 'text-warning',
  poor: 'text-danger',
};

function deliveryLine(shot: CapturedShot, online: boolean): string {
  const head = shot.frameCount > 1 ? `Queued ${shot.frameCount} frames.` : 'Queued.';
  if (shot.delivery === 'refused') return shot.message || 'The capture was not accepted.';
  if (shot.delivery === 'uploading') return `${head} Uploading now.`;
  if (shot.delivery === 'uploaded') {
    return shot.frameCount > 1
      ? `Uploaded ${shot.frameCount} frames. Extraction is running.`
      : 'Uploaded. Extraction is running.';
  }
  return online ? `${head} Waiting for the next upload pass.` : `${head} Uploads once you have a connection.`;
}

/**
 * The strip tells the operator what each frame will be worth before the
 * capture goes anywhere, which is what a colour on the thumbnail is for.
 * Anything the gate was content with stays quiet.
 */
const GRADE_RING: Record<QualityGrade, string> = {
  excellent: 'ring-border-strong',
  good: 'ring-border-strong',
  acceptable: 'ring-warning',
  poor: 'ring-danger',
};

export function CaptureViewfinder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId = '' } = useParams();
  const jobPath = `/jobs/${encodeURIComponent(jobId)}`;
  // Runs before the state below reads the held frames, so a set collected
  // for another job never shows here.
  claimHeldFor(jobId);
  const entry = location.state as ViewfinderEntryState | null;
  // Read once on mount: a device does not grow a touchscreen mid-capture.
  const [touchDevice] = useState(prefersCameraCapture);
  const [cameraRequested, setCameraRequested] = useState(touchDevice);
  const {
    videoRef,
    ready,
    error: cameraError,
    capturePhoto,
    canFlip,
    flip,
    zoom,
    setZoom,
  } = useCamera({ enabled: cameraRequested });
  const [jobName, setJobName] = useState<string | null>(() => knownJobName(jobId));
  const [note, setNote] = useState<HeldNote | null>(heldNote);
  const [mode, setMode] = useState<SingleShotMode>('photo');
  const [barcodeEnabled, setBarcodeEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(
    entry?.keptFromRetake ? KEPT_MESSAGE[entry.keptFromRetake] : null,
  );
  const [shot, setShot] = useState<CapturedShot | null>(null);
  const [dragging, setDragging] = useState(false);
  // The frames collected so far. Held outside React (`lib/capture-frames`)
  // because CA-04 is a route and going there unmounts this screen, so this
  // is a view of that holder rather than the holder itself.
  const [frames, setFrames] = useState<CaptureFrame[]>(listFrames);
  // Read once per visit. Changing it goes through /capture/workflow, which
  // navigates back here and remounts.
  const [workflow] = useState(readStoredWorkflow);
  const online = useOnlineStatus();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const phoneCameraInputRef = useRef<HTMLInputElement>(null);

  // The name is shown back on the bar above the frame. The device usually
  // knows it from My jobs; a viewfinder opened straight from a URL asks.
  // Offline it stays unnamed, and the capture still carries the job id.
  useEffect(() => {
    if (jobName) {
      rememberCurrentJob({ jobId, name: jobName });
      return;
    }
    let live = true;
    getCaptureJob(jobId)
      .then((job) => {
        if (live) setJobName(job.name);
      })
      .catch(() => {
        // Nothing to show but the link back to the job.
      });
    return () => {
      live = false;
    };
  }, [jobId, jobName]);

  function holdNote(next: HeldNote | null) {
    heldNote = next;
    setNote(next);
  }

  // One object URL for the queued capture's lead frame, released when the
  // next capture replaces it. The dependency is the URL string rather than
  // the shot, so the delivery transition that follows the upload does not
  // revoke the image under it.
  useEffect(() => {
    const url = shot?.url;
    if (!url) return;
    return () => URL.revokeObjectURL(url);
  }, [shot?.url]);

  // A URL per thumbnail, rebuilt whenever the set changes. Three blobs is
  // the ceiling, so rebuilding the lot costs less than tracking which one
  // moved, and every URL this effect made is the one it revokes.
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  useEffect(() => {
    const urls: Record<string, string> = {};
    for (const frame of frames) {
      urls[frame.fingerprint] = URL.createObjectURL(frame.bytes);
    }
    setThumbnails(urls);
    return () => {
      for (const url of Object.values(urls)) URL.revokeObjectURL(url);
    };
  }, [frames]);

  // The first capture of a session names its job, which is the step the
  // proof of concept opened on. After that the choice is remembered and the
  // bar above the frame shows it back.
  useEffect(() => {
    if (!workflow && !entry?.keptFromRetake) {
      navigate(`${jobPath}/capture/workflow`, { replace: true });
    }
  }, [workflow, entry, navigate, jobPath]);

  // History state outlives a reload, so the confirmation is read once and
  // dropped. Leaving it there tells an operator who refreshes the page that
  // they just queued something, which by then is no longer news.
  useEffect(() => {
    if (entry?.keptFromRetake) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [entry, location.pathname, navigate]);

  // The shutter can only produce a frame while a stream is live. Anywhere
  // else the file picker carries the screen instead of sitting under a dead
  // primary button.
  const cameraUnavailable = Boolean(cameraError) || !cameraRequested;

  async function handleShutter() {
    setBusy(true);
    setStatus(null);
    setShot(null);
    try {
      const blob = await capturePhoto();
      if (!blob) {
        setStatus('No frame captured. Point the camera at the item and try again.');
        return;
      }
      await ingest(blob);
    } finally {
      setBusy(false);
    }
  }

  async function handleFileChosen(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Clear the input first, so choosing the same file twice in a row still
    // fires a change event.
    event.target.value = '';
    await acceptFile(file);
  }

  async function handleDrop(event: React.DragEvent) {
    event.preventDefault();
    setDragging(false);
    await acceptFile(event.dataTransfer.files?.[0]);
  }

  /** The one entry point for a file, picked or dropped. A PDF is accepted
   *  in document mode only, ahead of the image check below: it is never an
   *  image and `createImageBitmap` would just refuse it. */
  async function acceptFile(file: File | undefined) {
    if (!file) return;

    if (mode === 'document' && looksLikePdf(file)) {
      setBusy(true);
      setStatus(null);
      setShot(null);
      try {
        await ingestDocument(file);
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!looksLikeImage(file)) {
      setStatus('That file is not an image. Choose a photo of the item, or a single-page PDF in Document mode.');
      return;
    }

    setBusy(true);
    setStatus(null);
    setShot(null);
    try {
      await ingest(file);
    } catch {
      // Whether this browser decodes the format is settled by trying it, not
      // by its extension: Safari reads HEIC and the other three engines do
      // not. The message names the format so the instruction is actionable.
      setStatus(decodeFailureMessage(file));
    } finally {
      setBusy(false);
    }
  }

  /**
   * A document-mode PDF (INTERFACES.md 15.6). No pixels to gate or decode
   * a barcode from, so this skips straight to a frame: a quality result
   * that trivially passes, because the on-device gate is a pixel check
   * with nothing here to read, and the true validation (single page or
   * not) is the server's on `POST /capture/upload-url`.
   */
  async function ingestDocument(file: File) {
    const fingerprint = await sha256Fingerprint(file);
    const frame: CaptureFrame = {
      fingerprint,
      contentType: file.type || DOCUMENT_CONTENT_TYPE,
      bytes: file,
      qualityResult: { passed: true, checks: [] },
      advice: { grade: 'excellent', shouldStop: false, suggestions: [] },
      manualFields: {},
    };
    const outcome = addFrame(frame);
    if (outcome !== 'added') {
      setStatus(OUTCOME_MESSAGE[outcome]);
      return;
    }
    setFrames(listFrames());
  }

  /**
   * The shared path from pixels to the offline queue. Both the shutter and the
   * file picker land here, so a chosen image is gated, fingerprinted and
   * decoded exactly as a shot one is.
   */
  async function ingest(original: Blob) {
    // Shrunk first, so the fingerprint, the gate and the queued bytes all
    // describe the image that is actually uploaded (src/lib/downscale-image.ts).
    const blob = await downscaleForUpload(original);
    // Fingerprint BEFORE any network call. A duplicate asset never
    // uploads its bytes (src/lib/api/capture.ts uploadCaptureAsset).
    const fingerprint = await sha256Fingerprint(blob);

    const bitmap = await createImageBitmap(blob);
    const sample = await sampleImage(bitmap);
    const qualityResult: QualityGateResult = runQualityGate(sample);

    const manualFields: Record<string, string> = {};
    if (barcodeEnabled) {
      const decoded = await decodeBarcodeFromImage(bitmap);
      if (decoded) {
        manualFields.barcodeValue = decoded.value;
        manualFields.barcodeSymbology = decoded.symbology;
      }
    }

    // The gate advises and the operator decides, which is how the Base44
    // proof of concept behaved and what this module's own header requires.
    // A frame that failed most of its checks and is soft or badly lit is the
    // one case that still holds the capture up, matching the POC's stop.
    const advice = adviseOnQuality(qualityResult);
    const frame: CaptureFrame = {
      fingerprint,
      contentType: blob.type || 'image/jpeg',
      bytes: blob,
      qualityResult,
      advice,
      manualFields,
    };

    if (advice.shouldStop) {
      // The bytes travel in the module-scoped holder, never in navigation
      // state, which is structured-cloned into session history. The decision
      // screen needs them to show the frame and to add it to the set if the
      // operator keeps it, and holding an empty Blob instead is what made
      // every gate-failing capture unuploadable.
      holdForDecision(frame, mode);
      navigate(`${jobPath}/capture/retake`);
      return;
    }

    const outcome = addFrame(frame);
    if (outcome !== 'added') {
      setStatus(OUTCOME_MESSAGE[outcome]);
      return;
    }
    setFrames(listFrames());
  }

  /**
   * Sends the frames as one capture.
   *
   * The capture's own fingerprint is over the ordered frame fingerprints, so
   * the same angles resubmitted land on the same capture; each frame still
   * presigns on its own, which is what lets a frame the tenant already holds
   * skip its bytes.
   */
  async function queueFrames() {
    const held = listFrames();
    if (held.length === 0 || busy) return;

    setBusy(true);
    setStatus(null);
    try {
      const lead = held[0];
      // The frame goes up in front of the operator before the upload starts,
      // so the screen answers the action at once and the delivery line
      // catches up underneath it.
      setShot({
        url: URL.createObjectURL(lead.bytes),
        contentType: lead.contentType,
        advice: lead.advice.suggestions.length > 0 ? lead.advice : null,
        frameCount: held.length,
        delivery: online ? 'uploading' : 'waiting',
        // Set below once `enqueueAndDeliver` hands back the `queue` row's
        // key, which happens before any network call.
        localId: '',
      });

      const fingerprint = await captureFingerprint(held.map((frame) => frame.fingerprint));
      // The submission carries one gate result and has no slot per asset, so
      // a capture holding a frame the gate failed reports that frame. Sending
      // the lead frame's pass instead would tell the server the capture was
      // clean while a flagged frame travelled inside it.
      const reportedQuality = held.find((frame) => !frame.qualityResult.passed) ?? lead;
      // Every frame's barcode read travels, because a barcode on the second
      // angle identifies the item just as well as one on the first.
      const manualFields = held.reduce<Record<string, string>>(
        (all, frame) => ({ ...all, ...frame.manualFields }),
        {},
      );

      const outcome = await enqueueAndDeliver({
        captureJobId: jobId,
        mode,
        ...(workflow ? { workflow } : {}),
        fingerprint,
        assets: held.map((frame) => ({
          fingerprint: frame.fingerprint,
          contentType: frame.contentType,
          bytes: frame.bytes,
        })),
        sessionId: getSessionId(),
        clientQuality: reportedQuality.qualityResult,
        ...(Object.keys(manualFields).length > 0 ? { manualFields } : {}),
      });

      // The voice note saved at the start of this capture goes with it. It
      // keys to the queue row until the capture has a server id, and
      // registers as soon as it has one.
      const sentNote = heldNote;
      if (sentNote) {
        await enqueueTranscriptNote({
          ...(outcome.captureId ? { captureId: outcome.captureId } : { queuedCaptureLocalId: outcome.localId }),
          transcript: sentNote.transcript,
          durationMs: sentNote.durationMs,
          purpose: 'NOTE',
        });
        holdNote(null);
        if (outcome.captureId) void processVoiceNoteQueue();
      }

      clearFrames();
      setFrames([]);
      setShot((current) =>
        current
          ? {
              ...current,
              delivery: outcome.state,
              localId: outcome.localId,
              ...(outcome.captureId ? { captureId: outcome.captureId } : {}),
              ...(outcome.message ? { message: outcome.message } : {}),
            }
          : current,
      );
    } finally {
      setBusy(false);
    }
  }

  function dropFrame(fingerprint: string) {
    removeFrame(fingerprint);
    setFrames(listFrames());
  }

  return (
    /*
      The viewfinder is the one bold surface on this screen, so it takes the
      space and everything around it stays quiet. On a phone the controls
      stack under it, within thumb reach. From the tablet breakpoint they sit
      beside it, because a 3:4 frame plus a stack of buttons is taller than a
      tablet viewport and pushes the shutter off screen. The operator has to
      see the shot and the shutter at once.
    */
    <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[minmax(0,1fr)_14rem] sm:items-start sm:gap-5 lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-6">
      {/* The job every capture from this screen joins, and the purpose the
          model will be told about. A remembered choice is never a silent
          one, so both are named here, each beside the way to change it. */}
      <div className="flex flex-col sm:col-span-2">
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 truncate text-sm font-medium text-surface-foreground">
            <span className="text-xs font-normal text-muted">Job </span>
            {jobName ?? 'this job'}
          </p>
          <Link
            to={jobPath}
            className="inline-flex min-h-11 shrink-0 items-center text-xs text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Instructions
          </Link>
        </div>
        {workflow && (
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-xs text-muted">
              Capturing for <span className="text-surface-foreground">{workflowTitle(workflow)}</span>
            </p>
            <Link
              to={`${jobPath}/capture/workflow`}
              className="inline-flex min-h-11 shrink-0 items-center text-xs text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              Change
            </Link>
          </div>
        )}
      </div>
      {/*
        The frame is capped by viewport height, expressed as a width, because
        a max-height on an aspect-ratio box squashes the box instead of
        scaling it and stops the preview showing the real capture shape.
        Height is 4/3 of width, so 48vh of width is 64vh of height.
      */}
      {/* With no live stream the frame is dead space on a desktop, so it
          doubles as the drop target: dragging a photo onto the shape the
          capture will take is the gesture a pointer device already has. */}
      <div
        onDragOver={cameraUnavailable ? (event) => { event.preventDefault(); setDragging(true); } : undefined}
        onDragLeave={cameraUnavailable ? () => setDragging(false) : undefined}
        onDrop={cameraUnavailable ? (event) => void handleDrop(event) : undefined}
        className={cn(
          'relative mx-auto aspect-[3/4] w-full max-w-sm overflow-hidden rounded-card border border-border bg-black/40 sm:col-start-1 sm:row-span-6 sm:mx-0 sm:max-w-[48vh] lg:max-w-[54vh]',
          dragging && 'border-accent',
        )}
      >
        <video ref={videoRef} className="size-full object-cover" muted playsInline />
        {!cameraRequested && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
            <ImageUp className="size-8 text-muted" aria-hidden="true" />
            <p className="text-sm text-muted">
              {dragging ? 'Drop the photo to capture it.' : 'Drag a photo here, or choose one from this computer.'}
            </p>
          </div>
        )}
        {cameraRequested && !ready && !cameraError && (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-muted">
            Starting camera…
          </p>
        )}
        {cameraRequested && cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
            <p className="text-sm text-warning">{cameraError}</p>
            <p className="text-sm text-muted">
              {touchDevice ? 'Use the phone camera, or choose an image instead.' : 'Choose an image file instead.'}
            </p>
          </div>
        )}
        {/* Lens controls sit on the live frame, in the corner the thumb
            does not cover while it rests on the shutter. The flip shows
            only where there is another camera to flip to, and the zoom only
            where the track reports a zoom range. */}
        {cameraRequested && ready && !shot && (canFlip || zoom) && (
          <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-2 p-2">
            {zoom ? (
              <label className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-control bg-surface/80 px-3 text-xs text-surface-foreground sm:max-w-56">
                <ZoomIn className="size-4 shrink-0" aria-hidden="true" />
                <span className="sr-only">Zoom</span>
                <input
                  type="range"
                  min={zoom.min}
                  max={zoom.max}
                  step={zoom.step}
                  value={zoom.value}
                  onChange={(event) => setZoom(Number(event.target.value))}
                  className="min-w-0 flex-1 accent-[var(--color-brand)]"
                />
                <span className="w-9 shrink-0 text-right tabular-nums">{zoom.value.toFixed(1)}x</span>
              </label>
            ) : (
              <span />
            )}
            {canFlip && (
              <button
                type="button"
                onClick={flip}
                aria-label="Switch camera"
                className="flex size-11 shrink-0 items-center justify-center rounded-control bg-surface/80 text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <SwitchCamera className="size-5" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {shot && (
          <div className="absolute inset-0 flex flex-col bg-surface">
            {/* A PDF object URL renders nothing inside an `<img>`, so a
                document-mode capture shows the icon a file browser would
                rather than a broken image. */}
            {shot.contentType === DOCUMENT_CONTENT_TYPE ? (
              <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 text-muted">
                <FileText className="size-10" aria-hidden="true" />
                <p className="text-xs">PDF document</p>
              </div>
            ) : shot.url ? (
              <img
                src={shot.url}
                alt="The frame you just captured"
                className="min-h-0 flex-1 object-contain"
              />
            ) : (
              <div className="min-h-0 flex-1" />
            )}
            <div className="border-t border-border-strong p-3">
              <p
                className={cn(
                  'text-sm font-medium',
                  shot.delivery === 'refused' ? 'text-danger' : 'text-surface-foreground',
                )}
                role={shot.delivery === 'refused' ? 'alert' : undefined}
              >
                {deliveryLine(shot, online)}
              </p>
              {shot.advice && (
                <>
                  <p className={cn('mt-2 text-xs font-medium', GRADE_TONE[shot.advice.grade])}>
                    {gradeLabel(shot.advice.grade)}
                  </p>
                  <ul className="mt-1 flex flex-col gap-1">
                    {shot.advice.suggestions.map((suggestion) => (
                      <li key={suggestion} className="text-xs text-muted">
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-x-4">
                {/* An arrived capture is worth checking while the operator
                    can still pick the item up, which is the whole reason
                    CA-05 exists and the only screen that reaches it was the
                    queue. One that has not arrived has nothing to show yet,
                    so that one still points at the queue. */}
                {shot.delivery === 'refused' ? (
                  <button
                    type="button"
                    onClick={() => {
                      void removeQueuedCapture(shot.localId).then(() => setShot(null));
                    }}
                    className="inline-flex min-h-11 items-center text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    Discard this capture
                  </button>
                ) : shot.captureId ? (
                  <Link
                    to={`/capture/${shot.captureId}/result`}
                    className="inline-flex min-h-11 items-center text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    Check what was read
                  </Link>
                ) : (
                  <Link
                    to="/queue"
                    className="inline-flex min-h-11 items-center text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    Track it in the queue
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => setShot(null)}
                  className="inline-flex min-h-11 items-center text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  Capture the next item
                </button>
              </div>
            </div>
          </div>
        )}

        {/*
          The frames of the capture being built, on the frame box itself,
          because that is where the operator is looking and a strip below
          the shutter is off a 390px screen. Each thumbnail carries what the
          gate made of it, so a soft frame is visible as one before the
          capture goes anywhere.
        */}
        {!shot && frames.length > 0 && (
          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 border-t border-border-strong bg-surface/95 p-3">
            <p className="text-xs text-muted">
              {frames.length < MAX_FRAMES
                ? 'Shoot another angle, or send what you have.'
                : `${MAX_FRAMES} frames is one capture. Send it, or remove one.`}
            </p>
            <div className="flex items-center gap-2">
              <ul className="flex min-w-0 flex-1 items-center gap-2">
                {frames.map((frame, index) => (
                  <li key={frame.fingerprint} className="relative">
                    {frame.contentType === DOCUMENT_CONTENT_TYPE ? (
                      <div
                        className={cn(
                          'flex size-14 items-center justify-center rounded-control bg-surface ring-1',
                          GRADE_RING[frame.advice.grade],
                        )}
                      >
                        <FileText className="size-5 text-muted" aria-hidden="true" />
                      </div>
                    ) : (
                      <img
                        src={thumbnails[frame.fingerprint]}
                        alt={`Frame ${index + 1} of this capture`}
                        className={cn(
                          'size-14 rounded-control object-cover ring-1',
                          GRADE_RING[frame.advice.grade],
                        )}
                      />
                    )}
                    {/* The control sits inside the thumbnail rather than
                        on its corner: a 44px target hung outside a 56px
                        thumbnail reaches over the frame beside it, and the
                        one the operator taps is then whichever the browser
                        stacks on top. */}
                    <button
                      type="button"
                      onClick={() => dropFrame(frame.fingerprint)}
                      aria-label={`Remove frame ${index + 1}`}
                      className="absolute right-0 top-0 flex size-11 items-start justify-end focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      <span className="m-1 flex size-5 items-center justify-center rounded-full border border-border-strong bg-surface/90 text-muted">
                        <X className="size-3" aria-hidden="true" />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <Button size="sm" disabled={busy} onClick={() => void queueFrames()}>
                {busy ? 'Sending…' : `Send ${frames.length === 1 ? 'capture' : `${frames.length} frames`}`}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Refusals only. A file this browser cannot decode, a frame the
          capture cannot take, a shutter that produced no frame. Each sits
          directly under the frame box, because the stack below it runs off
          the bottom of a 390px screen and a refusal the operator never sees
          reads as a control that did nothing. Everything a queued capture
          has to say is in the frame itself. */}
      {status && (
        <div className="sm:col-start-2">
          <p className="text-balance text-center text-xs text-warning sm:text-left">{status}</p>
        </div>
      )}

      <div className="flex gap-2 sm:col-start-2 sm:flex-col">
        <ModeButton active={mode === 'document'} onClick={() => setMode('document')} icon={FileText} label="Document" />
        <ModeButton active={mode === 'photo'} onClick={() => setMode('photo')} icon={Grid3x3} label="Single" />
        <ModeButton
          active={false}
          onClick={() => navigate(`${jobPath}/capture/rapid`)}
          icon={Grid3x3}
          label="Rapid"
        />
      </div>

      <div className="flex items-center justify-between text-xs text-muted sm:col-start-2">
        <button type="button" onClick={() => setBarcodeEnabled((v) => !v)} className="flex items-center gap-1.5">
          <ScanBarcode className="size-4" />
          Barcode {barcodeEnabled ? 'on' : 'off'}
        </button>
      </div>

      {/*
        `accept` names no HEIC or HEIF on purpose. iOS Safari transcodes a
        chosen photo to JPEG for a file input, and hands over the untouched
        HEIC as soon as the input says it takes one. Listing them here was
        what put HEIC on the iPhone path in the first place.

        Document mode also accepts a PDF (INTERFACES.md 15.6), the form a
        compliance certificate or a supplier invoice already exists in.
      */}
      {/* The native camera, for the full lens and zoom range a browser
          stream does not reach (CAPTURE_JOBS.md section 6). Its photo runs
          the same pipeline as the shutter's. */}
      <input
        ref={phoneCameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => void handleFileChosen(event)}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={mode === 'document' ? `image/*,${DOCUMENT_CONTENT_TYPE}` : 'image/*'}
        className="sr-only"
        onChange={(event) => void handleFileChosen(event)}
      />

      <div className="flex flex-col gap-2 sm:col-start-2">
        {!cameraUnavailable && (
          <Button size="lg" disabled={!ready || busy} onClick={() => void handleShutter()}>
            {busy ? 'Processing…' : 'Shoot'}
          </Button>
        )}
        {touchDevice && (
          <Button
            size="lg"
            variant={cameraUnavailable ? 'primary' : 'outline'}
            disabled={busy}
            onClick={() => phoneCameraInputRef.current?.click()}
          >
            <Camera className="size-4" />
            Use phone camera
          </Button>
        )}
        <Button
          size="lg"
          variant={cameraUnavailable && !touchDevice ? 'primary' : 'outline'}
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageUp className="size-4" />
          {busy && cameraUnavailable && !touchDevice ? 'Processing…' : 'Choose image'}
        </Button>
        {/* A desk with a webcam is still allowed to use it, on request. The
            prompt then answers an action the operator took. */}
        {!cameraRequested && (
          <Button size="lg" variant="outline" disabled={busy} onClick={() => setCameraRequested(true)}>
            <Camera className="size-4" />
            Use camera
          </Button>
        )}
      </div>

      {/* The voice note for the capture being built (CAPTURE_JOBS.md
          sections 3 and 6). Spoken at the start, while the operator is
          looking at the item, and sent with the frames. It sits below the
          shutter, because on a phone a transcript box above it pushed Shoot
          below the fold. */}
      <section className="flex flex-col gap-2 sm:col-start-2" aria-labelledby="capture-voice-note">
        <h2 id="capture-voice-note" className="text-xs font-medium text-muted">
          Voice note for this capture
        </h2>
        {note ? (
          <div className="flex flex-col gap-1 rounded-control border border-border p-3">
            <p className="whitespace-pre-line text-sm text-surface-foreground">{note.transcript}</p>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted">Goes with this capture when you send it.</p>
              <button
                type="button"
                onClick={() => holdNote(null)}
                className="inline-flex min-h-11 shrink-0 items-center text-xs text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <LiveTranscriptBox onSave={(transcript, durationMs) => holdNote({ transcript, durationMs })} />
        )}
      </section>
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof FileText;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex min-h-14 flex-1 flex-col items-center justify-center gap-1 rounded-control border border-border py-2 text-xs text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        active && 'border-brand text-brand',
      )}
    >
      <Icon className="size-4" />
      {label}
    </button>
  );
}
